import { useTranslation } from "react-i18next";
import UserStatus from "enums/UserStatus";
import { Chip, StatusGlyph } from "@outpave/ds";
import type { ChipTone } from "@outpave/ds";

interface RenderUserStatusProps {
  userStatus: string;
}

/**
 * One user-status pill for every surface that draws one. There were two and
 * they disagreed: the admin table drew a heroicon plus an untranslated span,
 * the platform table a Chip whose PENDING was `brand`.
 *
 * The fall-through is `neutral`: a status this map has not heard of is
 * unknown, not failed.
 */
const statusMapping: Record<string, { name: string; chipTone: ChipTone }> = {
  [UserStatus.ACTIVE]: { name: "Active", chipTone: "positive" },
  [UserStatus.PENDING]: { name: "Pending", chipTone: "info" },
  [UserStatus.INACTIVE]: { name: "Inactive", chipTone: "warning" },
  default: { name: "Unknown Status", chipTone: "neutral" },
};

export const RenderUserStatus: React.FC<RenderUserStatusProps> = ({
  userStatus,
}) => {
  const { t } = useTranslation();

  const { name, chipTone } = statusMapping[userStatus] ?? statusMapping.default;

  // StatusGlyph is looked up by the untranslated `name`, so the glyph is right
  // in every locale while the label renders translated.
  return (
    <Chip
      tone={chipTone}
      size="sm"
      leading={<StatusGlyph status={name} tone={chipTone} />}
    >
      {t(name)}
    </Chip>
  );
};

export default RenderUserStatus;
